import { useUser } from "@features/user"
import type React from "react"
import type { ReactNode } from "react"

// Define the props for ProtectedRoute, which expects children of type ReactNode
interface ProtectedRouteProps {
  children: ReactNode
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { isLoading, data, isFetching } = useUser()

  // Only show loading spinner on INITIAL load (no cached data)
  // If we have cached data, show the UI immediately while revalidating in background
  const showLoader = isLoading && !data

  if (showLoader) {
    return (
      <div className="flex items-center justify-center w-full h-screen">
        <div className="flex justify-center items-center space-x-1 text-sm text-gray-700">
          <svg
            fill="none"
            className="w-16 h-16 animate-spin"
            viewBox="0 0 32 32"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M15.165 8.53a.5.5 0 01-.404.58A7 7 0 1023 16a.5.5 0 011 0 8 8 0 11-9.416-7.874.5.5 0 01.58.404z"
              fill="currentColor"
              fillRule="evenodd"
            />
          </svg>
        </div>
        <div>Loading</div>
      </div>
    )
  }

  // Show UI with cached data (even while revalidating in background)
  // Optional: Show a subtle indicator when revalidating
  return (
    <>
      {isFetching && !isLoading && (
        <div className="fixed top-0 left-0 right-0 h-1 bg-blue-500 animate-pulse z-50" />
      )}
      {children}
    </>
  )
}

export default ProtectedRoute
